import React from "react";
import HetviGirl from "./HetviGirl";

/**
 * The cinematic background scene. All visual elements receive a `visible`
 * class based on the chapter. Everything transitions via CSS opacity / transform.
 */

const Stars = ({ visible }) => {
  // Deterministic pseudo-random positions
  const stars = React.useMemo(() => {
    const out = [];
    let seed = 13;
    for (let i = 0; i < 60; i++) {
      seed = (seed * 9301 + 49297) % 233280;
      const x = (seed / 233280) * 100;
      seed = (seed * 9301 + 49297) % 233280;
      const y = (seed / 233280) * 55;
      seed = (seed * 9301 + 49297) % 233280;
      const s = (seed / 233280) * 1.6 + 0.4;
      out.push({ x, y, s, d: i * 0.13 });
    }
    return out;
  }, []);
  return (
    <div className={`stars ${visible ? "on" : "off"}`}>
      {stars.map((st, i) => (
        <span
          key={i}
          className="star"
          style={{
            left: `${st.x}%`,
            top: `${st.y}%`,
            width: `${st.s}px`,
            height: `${st.s}px`,
            animationDelay: `${st.d}s`,
          }}
        />
      ))}
    </div>
  );
};

const Rain = ({ intensity }) => {
  // intensity: 0 none, 1 light, 2 heavy
  const count = intensity === 0 ? 0 : intensity === 1 ? 40 : 110;
  const drops = React.useMemo(() => {
    const out = [];
    let seed = 7;
    for (let i = 0; i < 120; i++) {
      seed = (seed * 9301 + 49297) % 233280;
      const x = (seed / 233280) * 100;
      seed = (seed * 9301 + 49297) % 233280;
      const d = (seed / 233280) * 1.4;
      seed = (seed * 9301 + 49297) % 233280;
      const dur = 0.55 + (seed / 233280) * 0.6;
      out.push({ x, d, dur });
    }
    return out;
  }, []);
  return (
    <div className={`rain rain-i-${intensity}`} aria-hidden>
      {drops.slice(0, count).map((dr, i) => (
        <span
          key={i}
          className="raindrop"
          style={{
            left: `${dr.x}%`,
            animationDelay: `${dr.d}s`,
            animationDuration: `${dr.dur}s`,
          }}
        />
      ))}
    </div>
  );
};

const Clouds = ({ kind, visible }) => {
  // kind: "storm" | "fluffy"
  const arr = [0, 1, 2, 3];
  return (
    <div className={`clouds clouds-${kind} ${visible ? "on" : "off"}`}>
      {arr.map((i) => (
        <div key={i} className={`cloud cloud-${i}`}>
          <span />
          <span />
          <span />
        </div>
      ))}
    </div>
  );
};

const Sun = ({ visible }) => (
  <div className={`sun ${visible ? "on" : "off"}`} aria-hidden>
    <div className="sun-rays" />
    <div className="sun-core" />
    <div className="sun-glow" />
  </div>
);

const Moon = ({ visible }) => (
  <div className={`moon ${visible ? "on" : "off"}`} aria-hidden>
    <div className="moon-core" />
    <div className="moon-shadow" />
  </div>
);

const Rainbow = ({ visible }) => (
  <div className={`rainbow ${visible ? "on" : "off"}`} aria-hidden>
    <div className="arc a1" />
    <div className="arc a2" />
    <div className="arc a3" />
    <div className="arc a4" />
    <div className="arc a5" />
    <div className="arc a6" />
    <div className="arc a7" />
  </div>
);

const Trees = ({ lush }) => (
  <div className={`trees ${lush ? "lush" : ""}`} aria-hidden>
    <div className="tree tree-l">
      <div className="trunk" />
      <div className="canopy" />
    </div>
    <div className="tree tree-l tree-l2">
      <div className="trunk" />
      <div className="canopy" />
    </div>
    <div className="tree tree-r">
      <div className="trunk" />
      <div className="canopy" />
    </div>
    <div className="tree tree-r tree-r2">
      <div className="trunk" />
      <div className="canopy" />
    </div>
  </div>
);

const RoseBushes = ({ visible }) => (
  <div className={`rose-bushes ${visible ? "on" : "off"}`} aria-hidden>
    <div className="bush bush-l">
      {Array.from({ length: 6 }).map((_, i) => (
        <span key={i} className="rose" style={{ "--i": i }} />
      ))}
    </div>
    <div className="bush bush-r">
      {Array.from({ length: 6 }).map((_, i) => (
        <span key={i} className="rose" style={{ "--i": i }} />
      ))}
    </div>
  </div>
);

const Puddles = ({ visible }) => (
  <div className={`puddles ${visible ? "on" : "off"}`} aria-hidden>
    <div className="puddle p1" />
    <div className="puddle p2" />
    <div className="puddle p3" />
  </div>
);

const GroundFlowers = ({ visible }) => {
  const flowers = ["🌸", "🌼", "🌷", "🌺", "🌻", "🌹", "🌸", "🌼", "🌷"];
  return (
    <div className={`ground-flowers ${visible ? "on" : "off"}`} aria-hidden>
      {flowers.map((f, i) => (
        <span
          key={i}
          className="g-flower"
          style={{
            left: `${5 + i * 10}%`,
            animationDelay: `${i * 0.12}s`,
          }}
        >
          {f}
        </span>
      ))}
    </div>
  );
};

const FloatingPetals = ({ visible }) => {
  const items = Array.from({ length: 18 });
  return (
    <div className={`petals ${visible ? "on" : "off"}`} aria-hidden>
      {items.map((_, i) => {
        const left = (i * 53) % 100;
        const delay = (i % 6) * 0.6;
        const dur = 7 + (i % 5);
        const sym = i % 3 === 0 ? "🌹" : i % 3 === 1 ? "🌸" : "✿";
        return (
          <span
            key={i}
            className="petal"
            style={{
              left: `${left}%`,
              animationDelay: `${delay}s`,
              animationDuration: `${dur}s`,
            }}
          >
            {sym}
          </span>
        );
      })}
    </div>
  );
};

export const Scene = ({ chapter }) => {
  // Derive scene parameters from chapter index 0..6
  const skyClass = `sky-${chapter}`;
  const groundClass = `ground-${chapter}`;
  const starsOn = chapter <= 2 || chapter === 6;
  const moonOn = chapter <= 1 || chapter === 6;
  const rainIntensity = chapter <= 2 ? 2 : chapter === 3 ? 1 : 0;
  const stormCloudsOn = chapter >= 1 && chapter <= 3;
  const fluffyCloudsOn = chapter >= 4 && chapter <= 5;
  const rainbowOn = chapter >= 4 && chapter <= 5;
  const sunOn = chapter >= 4 && chapter <= 5;
  const roseBushesOn = chapter >= 2;
  const groundFlowersOn = chapter >= 4;
  const petalsOn = chapter >= 5;
  const treesLush = chapter >= 4;
  const puddlesOn = chapter <= 3;

  // Hetvi state
  let mood = "sad";
  if (chapter === 0) mood = "silhouette";
  else if (chapter <= 2) mood = "sad";
  else if (chapter === 3) mood = "sad";
  else if (chapter === 4) mood = "neutral";
  else if (chapter >= 5) mood = "happy";

  // chapter 0 — girl walks in from left
  const girlPosClass =
    chapter === 0
      ? "girl girl-walking"
      : chapter === 6
      ? "girl girl-hidden"
      : chapter <= 2
      ? "girl girl-sitting"
      : "girl girl-standing";

  return (
    <div className={`scene ${skyClass} ${groundClass}`}>
      <div className="sky-layer" />
      <Stars visible={starsOn} />
      <Moon visible={moonOn} />
      <Sun visible={sunOn} />
      <Rainbow visible={rainbowOn} />
      <Clouds kind="storm" visible={stormCloudsOn} />
      <Clouds kind="fluffy" visible={fluffyCloudsOn} />

      <div className="horizon" />
      <Trees lush={treesLush} />
      <RoseBushes visible={roseBushesOn} />

      <Rain intensity={rainIntensity} />

      <div className="ground-layer" />
      <Puddles visible={puddlesOn} />
      <GroundFlowers visible={groundFlowersOn} />
      <FloatingPetals visible={petalsOn} />

      <div className={girlPosClass}>
        <HetviGirl mood={mood} className="girl-svg" />
        {chapter !== 0 && chapter !== 6 && (
          <div className="girl-label">Hetvi</div>
        )}
      </div>

      {/* Grain overlay */}
      <div className="grain" aria-hidden />
      {/* Vignette */}
      <div className="vignette" aria-hidden />
    </div>
  );
};

export default Scene;
